import { syncSessionMetadata } from './auth-token-storage';
import type { TokenRefreshResult } from './api';
import store from '@/stores';

/** 同一オリジン内のタブ間でトークンリフレッシュを直列化する */
const LOCK_NAME = 'roomi-meet-auth-refresh';
const CHANNEL_NAME = 'roomi-meet-auth-refresh-v1';
const REMOTE_REFRESH_GRACE_MS = 3000;

type RefreshFinishMessage = {
  type: 'refresh-finish';
  ok: boolean;
  accessTokenExpiresAt?: number;
};

let channel: BroadcastChannel | null = null;
let remoteRefreshFinishedAt = 0;
let onRemoteRefreshSuccess: (() => void) | null = null;

/** 他タブのリフレッシュ成功時にタブ内のクールダウン状態を揃える（api から登録） */
export function setOnRemoteRefreshSuccess(handler: () => void): void {
  onRemoteRefreshSuccess = handler;
}

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<RefreshFinishMessage>) => {
      const msg = event.data;
      if (msg?.type !== 'refresh-finish' || !msg.ok) {
        return;
      }
      remoteRefreshFinishedAt = Date.now();
      if (typeof msg.accessTokenExpiresAt === 'number' && Number.isFinite(msg.accessTokenExpiresAt)) {
        syncSessionMetadata(msg.accessTokenExpiresAt);
      }
      onRemoteRefreshSuccess?.();
    };
  }
  return channel;
}

function notifyRemoteRefreshSuccess(accessTokenExpiresAt: number | null): void {
  remoteRefreshFinishedAt = Date.now();
  getBroadcastChannel()?.postMessage({
    type: 'refresh-finish',
    ok: true,
    accessTokenExpiresAt:
      typeof accessTokenExpiresAt === 'number' && Number.isFinite(accessTokenExpiresAt)
        ? accessTokenExpiresAt
        : undefined,
  } satisfies RefreshFinishMessage);
}

/** 他タブが直近でリフレッシュ済みなら HTTP を送らず成功扱いにする */
export function wasRefreshedRecentlyByAnotherTab(): boolean {
  if (remoteRefreshFinishedAt === 0) {
    return false;
  }
  if (Date.now() - remoteRefreshFinishedAt > REMOTE_REFRESH_GRACE_MS) {
    return false;
  }
  const exp = store.getState().authenticated.accessTokenExpiresAt;
  return typeof exp === 'number' && Number.isFinite(exp) && exp > Date.now();
}

/**
 * タブ内・タブ間で refresh を1本化する。
 * Web Locks API が使える場合はタブ横断の排他。結果は BroadcastChannel で他タブの Redux にも反映する。
 */
export async function coordinateTokenRefresh(
  executeRefresh: () => Promise<TokenRefreshResult>,
): Promise<TokenRefreshResult> {
  if (wasRefreshedRecentlyByAnotherTab()) {
    return { ok: true };
  }

  const runRefresh = async (): Promise<TokenRefreshResult> => {
    if (wasRefreshedRecentlyByAnotherTab()) {
      return { ok: true };
    }
    const result = await executeRefresh();
    if (result.ok) {
      const expiresAt = store.getState().authenticated.accessTokenExpiresAt;
      notifyRemoteRefreshSuccess(
        typeof expiresAt === 'number' && Number.isFinite(expiresAt) ? expiresAt : null,
      );
    }
    return result;
  };

  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(LOCK_NAME, runRefresh);
  }

  return runRefresh();
}
